/**
 * Settings modal component
 * Comprehensive settings interface for user preferences
 */

import React, { useState } from 'react';
import {
  X,
  Lock,
  Palette,
  MessageSquare,
  Brain,
  Eye,
  RotateCcw,
} from 'lucide-react';
import { usePreferences } from '@/context/PreferencesContext';
import { Button } from '@/components/Common/Button';
import { cn } from '@/utils/classNames';
import type { UserPreferences } from '@/types/preferences.types';

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type SettingsTab =
  | 'appearance'
  | 'chat'
  | 'ai'
  | 'privacy'
  | 'accessibility';

export function SettingsModal({ isOpen, onClose }: SettingsModalProps) {
  const [activeTab, setActiveTab] = useState<SettingsTab>('appearance');
  const { preferences, updatePreferences, resetPreferences } =
    usePreferences();

  if (!isOpen) return null;

  const tabs: Array<{
    id: SettingsTab;
    label: string;
    icon: React.ReactNode;
  }> = [
    {
      id: 'appearance',
      label: 'Appearance',
      icon: <Palette className="h-4 w-4" />
    },
    { id: 'chat', label: 'Chat', icon: <MessageSquare className="h-4 w-4" /> },
    { id: 'ai', label: 'AI Settings', icon: <Brain className="h-4 w-4" /> },
    { id: 'privacy', label: 'Privacy', icon: <Lock className="h-4 w-4" /> },
    { id: 'accessibility', label: 'Accessibility', icon: <Eye className="h-4 w-4" /> },
  ];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center
      bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="flex h-[600px] max-h-[90vh] w-full max-w-4xl
        overflow-hidden rounded-2xl bg-bg-primary shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Sidebar */}
        <div className="w-64 border-r border-border-default bg-bg-secondary p-4">
          <div className="mb-6 flex items-center justify-between">
            <h2 className="text-lg font-bold text-text-primary">Settings</h2>
            <button
              onClick={onClose}
              className="rounded-lg p-1.5 text-text-tertiary
              hover:bg-bg-tertiary hover:text-text-secondary"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <nav className="space-y-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  'flex w-full items-center gap-3 rounded-lg px-3 py-2',
                  'text-left text-sm font-medium transition-colors',
                  activeTab === tab.id
                    ? 'bg-stellar-primary text-white'
                    : 'text-text-secondary hover:bg-bg-tertiary'
                )}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </nav>

          <div className="mt-6 border-t border-border-default pt-4">
            <Button
              variant="secondary"
              size="sm"
              onClick={resetPreferences}
              leftIcon={<RotateCcw className="h-4 w-4" />}
              className="w-full"
            >
              Reset All
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'appearance' && (
            <AppearanceSettings
              preferences={preferences}
              updatePreferences={updatePreferences}
            />
          )}
          {activeTab === 'chat' && (
            <ChatSettings
              preferences={preferences}
              updatePreferences={updatePreferences}
            />
          )}
          {activeTab === 'ai' && (
            <AISettings
              preferences={preferences}
              updatePreferences={updatePreferences}
            />
          )}
          {activeTab === 'privacy' && (
            <PrivacySettings
              preferences={preferences}
              updatePreferences={updatePreferences}
            />
          )}
          {activeTab === 'accessibility' && (
            <AccessibilitySettings
              preferences={preferences}
              updatePreferences={updatePreferences}
            />
          )}
        </div>
      </div>
    </div>
  );
}

interface SettingsProps {
  preferences: UserPreferences;
  updatePreferences: (updates: Partial<UserPreferences>) => void;
}

function AppearanceSettings({
  preferences,
  updatePreferences,
}: SettingsProps) {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="mb-4 text-lg font-semibold text-text-primary">
          Appearance
        </h3>
      </div>

      <SettingItem
        label="Theme"
        description="Choose your preferred color theme"
      >
        <RadioGroup
          value={preferences.theme}
          onChange={(value) =>
            updatePreferences({
              theme: value as 'light' | 'dark' | 'system'
            })
          }
          options={[
            { value: 'light', label: 'Light' },
            { value: 'dark', label: 'Dark' },
            { value: 'system', label: 'System' },
          ]}
        />
      </SettingItem>

      <SettingItem
        label="Font Size"
        description="Adjust text size for better readability"
      >
        <RadioGroup
          value={preferences.fontSize}
          onChange={(value) =>
            updatePreferences({
              fontSize: value as 'small' | 'medium' | 'large'
            })
          }
          options={[
            { value: 'small', label: 'Small' },
            { value: 'medium', label: 'Medium' },
            { value: 'large', label: 'Large' },
          ]}
        />
      </SettingItem>

      <SettingItem
        label="Show Timestamps"
        description="Display message timestamps"
      >
        <Toggle
          checked={preferences.showTimestamps}
          onChange={(checked) =>
            updatePreferences({ showTimestamps: checked })
          }
        />
      </SettingItem>

      <SettingItem
        label="Message Grouping"
        description="Group consecutive messages from same sender"
      >
        <Toggle
          checked={preferences.messageGrouping}
          onChange={(checked) =>
            updatePreferences({ messageGrouping: checked })
          }
        />
      </SettingItem>

      <SettingItem
        label="Compact Mode"
        description="Reduce spacing for denser layout"
      >
        <Toggle
          checked={preferences.compactMode}
          onChange={(checked) => updatePreferences({ compactMode: checked })}
        />
      </SettingItem>
    </div>
  );
}

function ChatSettings({ preferences, updatePreferences }: SettingsProps) {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="mb-4 text-lg font-semibold text-text-primary">
          Chat Behavior
        </h3>
      </div>

      <SettingItem
        label="Send on Enter"
        description="Press Enter to send (Shift+Enter for new line)"
      >
        <Toggle
          checked={preferences.sendOnEnter}
          onChange={(checked) => updatePreferences({ sendOnEnter: checked })}
        />
      </SettingItem>

      <SettingItem
        label="Typing Indicator"
        description="Show when AI is generating response"
      >
        <Toggle
          checked={preferences.showTypingIndicator}
          onChange={(checked) =>
            updatePreferences({ showTypingIndicator: checked })
          }
        />
      </SettingItem>

      <SettingItem
        label="Sound Effects"
        description="Play sounds for message notifications"
      >
        <Toggle
          checked={preferences.enableSounds}
          onChange={(checked) =>
            updatePreferences({ enableSounds: checked })
          }
        />
      </SettingItem>

      <SettingItem
        label="Auto-Save Conversations"
        description="Automatically save chat history"
      >
        <Toggle
          checked={preferences.autoSaveConversations}
          onChange={(checked) =>
            updatePreferences({ autoSaveConversations: checked })
          }
        />
      </SettingItem>

      <SettingItem
        label="Keep Files Across Messages"
        description="Keep attached files after sending so they're reused in your next message. When off, files are cleared after each send."
      >
        <Toggle
          checked={preferences.keepFilesAcrossMessages}
          onChange={(checked) =>
            updatePreferences({ keepFilesAcrossMessages: checked })
          }
        />
      </SettingItem>
    </div>
  );
}

function AISettings({ preferences, updatePreferences }: SettingsProps) {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="mb-4 text-lg font-semibold text-text-primary">
          AI Configuration
        </h3>
      </div>

      <SettingItem
        label="Temperature"
        description="Controls randomness (0 = focused, 1 = creative)"
      >
        <div className="w-64">
          <input
            type="range"
            min="0"
            max="1"
            step="0.1"
            value={preferences.temperature}
            onChange={(e) =>
              updatePreferences({ temperature: parseFloat(e.target.value) })
            }
            className="w-full"
          />
          <div className="mt-1 text-sm text-text-secondary">
            {preferences.temperature.toFixed(1)}
          </div>
        </div>
      </SettingItem>

      <SettingItem
        label="Max Tokens"
        description="Maximum response length"
      >
        <input
          type="number"
          min="100"
          max="4000"
          step="100"
          value={preferences.maxTokens}
          onChange={(e) =>
            updatePreferences({ maxTokens: parseInt(e.target.value) })
          }
          className="w-32 rounded-lg border border-border-default px-3 py-2"
        />
      </SettingItem>

      <SettingItem
        label="Enable Citations"
        description="Show document sources in responses"
      >
        <Toggle
          checked={preferences.enableCitations}
          onChange={(checked) =>
            updatePreferences({ enableCitations: checked })
          }
        />
      </SettingItem>

      <SettingItem
        label="Enable File Upload"
        description="Allow uploading documents to conversations"
      >
        <Toggle
          checked={preferences.enableFileUpload}
          onChange={(checked) =>
            updatePreferences({ enableFileUpload: checked })
          }
        />
      </SettingItem>
    </div>
  );
}

function PrivacySettings({ preferences, updatePreferences }: SettingsProps) {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="mb-4 text-lg font-semibold text-text-primary">Privacy</h3>
      </div>

      <SettingItem
        label="Save History"
        description="Store conversation history locally"
      >
        <Toggle
          checked={preferences.saveHistory}
          onChange={(checked) => updatePreferences({ saveHistory: checked })}
        />
      </SettingItem>

      <SettingItem
        label="Share Analytics"
        description="Help improve the app by sharing anonymous usage data"
      >
        <Toggle
          checked={preferences.shareAnalytics}
          onChange={(checked) =>
            updatePreferences({ shareAnalytics: checked })
          }
        />
      </SettingItem>
    </div>
  );
}

function AccessibilitySettings({
  preferences,
  updatePreferences,
}: SettingsProps) {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="mb-4 text-lg font-semibold text-text-primary">
          Accessibility
        </h3>
      </div>

      <SettingItem
        label="High Contrast"
        description="Increase contrast for better visibility"
      >
        <Toggle
          checked={preferences.highContrast}
          onChange={(checked) =>
            updatePreferences({ highContrast: checked })
          }
        />
      </SettingItem>

      <SettingItem
        label="Reduced Motion"
        description="Minimize animations and transitions"
      >
        <Toggle
          checked={preferences.reducedMotion}
          onChange={(checked) =>
            updatePreferences({ reducedMotion: checked })
          }
        />
      </SettingItem>

      <SettingItem
        label="Screen Reader Optimized"
        description="Enhance compatibility with screen readers"
      >
        <Toggle
          checked={preferences.screenReaderOptimized}
          onChange={(checked) =>
            updatePreferences({ screenReaderOptimized: checked })
          }
        />
      </SettingItem>
    </div>
  );
}

function SettingItem({
  label,
  description,
  children,
}: {
  label: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-4 py-3">
      <div className="flex-1">
        <div className="font-medium text-text-primary">{label}</div>
        <div className="mt-1 text-sm text-text-tertiary">{description}</div>
      </div>
      <div className="flex-shrink-0">{children}</div>
    </div>
  );
}

function Toggle({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <button
      onClick={() => onChange(!checked)}
      className={cn(
        'relative inline-flex h-6 w-11 items-center rounded-full',
        'transition-colors focus:outline-none focus:ring-2',
        'focus:ring-stellar-primary focus:ring-offset-2',
        checked ? 'bg-stellar-primary' : 'bg-bg-tertiary'
      )}
    >
      <span
        className={cn(
          'inline-block h-4 w-4 transform rounded-full',
          'bg-white transition-transform',
          checked ? 'translate-x-6' : 'translate-x-1'
        )}
      />
    </button>
  );
}

function RadioGroup({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string }>;
}) {
  return (
    <div className="flex gap-2">
      {options.map((option) => (
        <button
          key={option.value}
          onClick={() => onChange(option.value)}
          className={cn(
            'rounded-lg border-2 px-4 py-2 text-sm font-medium',
            'transition-colors',
            value === option.value
              ? 'border-stellar-primary bg-bg-secondary text-text-primary'
              : 'border-border-default text-text-secondary hover:border-border-medium'
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}
